// =============================================================================
// Planilha Orçado vs Realizado
//
// Duas portas, uma tela: a aba "Orçado vs Realizado" da Visão financeira e a
// aba "Analítica" do Painel do Gestor. Validado com o Financeiro em 23/09.
//
// Os filtros moram AQUI, no cartão da Planilha orçamentária (Natureza, Visão,
// Mês, Ano, Centro de custo), e não no topo das outras abas — decisão do
// Financeiro: filtro de planilha é da planilha.
//
// Cada mês é um grupo de duas subcolunas, Orçado e Realizado, lado a lado. O
// recorte de quem vê o quê é da RLS: o gestor recebe só os CCs dele, e esta
// tela não filtra nada por papel.
//
// Pedidos do Financeiro de 20 e 21/10, todos aqui:
//   · agrupar a despesa por centro de custo OU por categoria — a categoria
//     somando todos os CCs, para conferir com o Conta Azul um pagamento rateado
//     entre áreas;
//   · receita SEMPRE por categoria, agrupada como no Conta Azul, porque a
//     receita de produto não tem área. Por CC ela chegou a ser opção e saiu no
//     mesmo dia, a pedido do Financeiro: "nunca por centro de custo";
//   · variação em duas colunas, R$ e %, no mês selecionado e no total do ano,
//     e o % em cada mês da visão do ano. Comprometido, saldo e consumo saíram
//     da visão do mês (o status continua, e ainda soma o comprometido);
//   · selo e filtro de conciliação: o que o motor confirmou sozinho e o que
//     espera confirmação (migration 064). O filtro saiu e voltou no mesmo dia,
//     a pedido do Financeiro (é o item 1.2 do pedido);
//   · coluna de variação; linhas sem valor no período NUNCA aparecem (houve uma
//     caixa para mostrá-las, que também saiu); o botão de lançamentos menor; e
//     as peças comuns de `Grade.tsx` — barra de filtros compacta e recolhível,
//     cabeçalho congelado, rolagem lateral sempre à vista, coluna dos nomes
//     ajustável, grupos que abrem e fecham, filtros em lista suspensa.
//
// Pedido da Mariana de 27/10: na despesa, a linha da categoria ABRE e mostra
// quem recebeu, mês a mês, na coluna Realizado — na folha e no PJ, cada
// colaborador. Decidido com o Enzo: abrir a linha aqui, com os doze meses, e
// não na Planilha orçamentária, que só tem colunas de orçado. A lupa continua,
// para o detalhe de um mês. O orçado fica na categoria: ele não é por
// fornecedor. Quem decide o que o gestor vê é a view (077), não esta tela.
// =============================================================================

import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buscarOrcadoRealizado, buscarRealizadoPorFornecedor, FalhaApi, type OrcadoFornecedor, type RealizadoFornecedor } from "../lib/api.ts";
import { agruparPorFornecedor, ORCADO_SEM_PESSOA, temOrcadoPorFornecedor, type LinhaFornecedor } from "../lib/fornecedoresDaLinha.ts";
import { LegendaEstados, Painel, Vazio } from "../componentes/ui.tsx";
import { FiltroAno, FiltroMes, resumoPeriodo } from "../componentes/FiltroPeriodo.tsx";
import {
  AbrirFecharTodos,
  AlcaLargura,
  BotaoGrupo,
  CaixaGrade,
  CampoCompacto,
  CLASSE_BOTAO_BARRA,
  FiltrosRecolhiveis,
  NomeNaLargura,
  Seletor,
  useLarguraColuna,
  useRecolhidos,
} from "../componentes/Grade.tsx";
import { Icone } from "../componentes/Icone.tsx";
import { baixarCsv, centavosParaCsv } from "../lib/csv.ts";
import { ModalLancamentos } from "./ModalLancamentos.tsx";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

type Valores = { orcado: bigint; realizado: bigint | null; comprometido: bigint | null };
/** A conciliação de uma célula (064). `null` = categoria de pessoal para quem
 *  não tem acesso total: a contagem não vem, e não há selo. */
type Conc = { lanc: number; auto: number; pessoa: number; aguardando: number; sem: number };
type Ref = { id: string; codigo: string | null; nome: string };
type Linha = {
  chave: string;
  /** Nulo na receita por categoria, que não se divide por centro de custo. */
  cc: Ref | null;
  cat: Ref;
  meses: Valores[];
  conc: (Conc | null | undefined)[];
};
type Grupo = {
  chave: string;
  rotulo: string;
  /** O que o drilldown do grupo abre: a categoria em todos os CCs. */
  cat: Ref | null;
  linhas: Linha[];
};

type Agrupar = "cc" | "categoria";
type FiltroConc = "todas" | "pendentes" | "automaticas";

const vazio = (): Valores => ({ orcado: 0n, realizado: null, comprometido: null });
const somaOpc = (a: bigint | null, b: bigint | null) => (a === null && b === null ? null : (a ?? 0n) + (b ?? 0n));
const somarValores = (vs: Valores[]): Valores =>
  vs.reduce((t, v) => ({
    orcado: t.orcado + v.orcado,
    realizado: somaOpc(t.realizado, v.realizado),
    comprometido: somaOpc(t.comprometido, v.comprometido),
  }), vazio());
const big = (v: string | null | undefined) => (v === null || v === undefined ? null : BigInt(v));

function somarConc(cs: (Conc | null | undefined)[]): Conc | null | undefined {
  let t: Conc | undefined;
  for (const c of cs) {
    if (c === null) return null;
    if (!c) continue;
    t = t
      ? { lanc: t.lanc + c.lanc, auto: t.auto + c.auto, pessoa: t.pessoa + c.pessoa, aguardando: t.aguardando + c.aguardando, sem: t.sem + c.sem }
      : { ...c };
  }
  return t;
}

/**
 * A receita como o Conta Azul a agrupa (pedido de 20/10, item 1.5). A
 * hierarquia sai do código pontuado do plano de contas (PLANO_DE_CONTAS.md §2):
 * os grupos intermediários não existem como registro, então o nome de cada um
 * mora aqui.
 */
const GRUPOS_RECEITA: readonly (readonly [string, string])[] = [
  ["1.01.01", "Receitas · Pós-graduação"],
  ["1.01.02", "Receitas · Cursos livres"],
  ["1.01.03", "Receitas · Graduação"],
  ["1.01.04", "Receitas · Outras receitas"],
  ["1.01", "Receitas · Outros produtos"],
  ["5.03", "Movimentação financeira"],
  ["6.01", "Receitas financeiras"],
];
function grupoReceita(codigo: string | null): { chave: string; rotulo: string; ordem: number } {
  if (!codigo) return { chave: "sem-codigo", rotulo: "Sem código no plano de contas", ordem: 98 };
  const i = GRUPOS_RECEITA.findIndex(([p]) => codigo === p || codigo.startsWith(`${p}.`));
  return i >= 0
    ? { chave: GRUPOS_RECEITA[i]![0], rotulo: GRUPOS_RECEITA[i]![1], ordem: i }
    : { chave: "outras", rotulo: "Outras categorias de receita", ordem: 97 };
}

const porCodigo = (a: Ref, b: Ref) =>
  (a.codigo ?? "~").localeCompare(b.codigo ?? "~", undefined, { numeric: true }) || a.nome.localeCompare(b.nome);

/** "1234567" centavos → "12.345,67", sem o "R$": a tabela já diz que é dinheiro. */
function curto(v: bigint | null): string {
  if (v === null) return "—";
  const neg = v < 0n;
  const a = neg ? -v : v;
  return `${neg ? "-" : ""}${new Intl.NumberFormat("pt-BR").format(a / 100n)},${(a % 100n).toString().padStart(2, "0")}`;
}
const comSinal = (v: bigint) => (v > 0n ? `+${curto(v)}` : curto(v));

/** Faixa de consumo — as cores da legenda. `usado` é realizado + comprometido
 *  no status e só realizado na célula do mês. */
function tom(orcado: bigint, usado: bigint | null): "ok" | "atencao" | "risco" | "neutro" | null {
  if (usado === null) return null;
  if (orcado === 0n) return usado > 0n ? "neutro" : null;
  const pct = Number((usado * 10000n) / orcado) / 100;
  return pct > 100 ? "risco" : pct >= 80 ? "atencao" : "ok";
}
const COR_TOM = { ok: "text-ok-tinta", atencao: "text-atencao-tinta", risco: "text-risco-tinta font-semibold", neutro: "text-neutro-tinta" };
const ROTULO_TOM = { ok: "Dentro", atencao: "Atenção", risco: "Acima", neutro: "Sem orçamento" };
const PILULA_TOM = {
  ok: "bg-ok-bg text-ok-tinta border-ok-linha",
  atencao: "bg-atencao-bg text-atencao-tinta border-atencao-linha",
  risco: "bg-risco-bg text-risco-tinta border-risco-linha",
  neutro: "bg-neutro-bg text-neutro-tinta border-neutro-linha",
};

type Detalhe = {
  centro_custo_id: string | null;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string | null;
  categoria_nome: string;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string;
  pct_execucao: string;
};

export function OrcadoRealizado({
  periodo,
  onMudarPeriodo,
  escopo,
}: {
  periodo: string[];
  onMudarPeriodo: (p: string[]) => void;
  /** Só muda textos: quem recorta os centros de custo é a RLS. */
  escopo: "financeiro" | "gestor";
}) {
  const ano = Number(periodo[periodo.length - 1]!.slice(0, 4));
  const [natureza, setNatureza] = useState<"despesa" | "receita">("despesa");
  const [visao, setVisao] = useState<"mes" | "ano">("mes");
  // A despesa escolhe; a receita é sempre por categoria, como no Conta Azul.
  const [agruparDespesa, setAgrupar] = useState<Agrupar>("cc");
  const agrupar: Agrupar = natureza === "receita" ? "categoria" : agruparDespesa;
  const [ccFiltro, setCcFiltro] = useState("todos");
  const [catFiltro, setCatFiltro] = useState("todas");
  const [concFiltro, setConcFiltro] = useState<FiltroConc>("todas");
  const [detalhando, setDetalhando] = useState<Detalhe | null>(null);
  // As linhas de categoria abertas por fornecedor, pela chave CC|categoria.
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());
  const alternarAberta = (chave: string) => setAbertas((s) => {
    const n = new Set(s);
    if (!n.delete(chave)) n.add(chave);
    return n;
  });
  const coluna = useLarguraColuna("orcado-realizado");
  const recolhidos = useRecolhidos();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ["orcado-realizado", ano, natureza],
    queryFn: () => buscarOrcadoRealizado(ano, natureza),
  });
  const receitaPorCategoria = natureza === "receita";

  // As linhas base: CC + categoria, doze meses cada (ou só a categoria, na
  // receita por categoria).
  const base = useMemo(() => {
    const porChave = new Map<string, Linha>();
    const nova = (chave: string, cc: Ref | null, cat: Ref) => {
      let r = porChave.get(chave);
      if (!r) {
        r = { chave, cc, cat, meses: MESES.map(vazio), conc: MESES.map(() => undefined) };
        porChave.set(chave, r);
      }
      return r;
    };
    if (receitaPorCategoria) {
      const cats = new Map((q.data?.categorias ?? []).map((c) => [c.categoria_id, c]));
      for (const c of q.data?.categorias ?? []) nova(c.categoria_id, null, { id: c.categoria_id, codigo: c.codigo, nome: c.nome });
      // Categoria que não recebe orçamento (desativada, fora do orçamento) mas
      // teve recebimento: entra com o nome que a linha de CC trouxer.
      const nomes = new Map((q.data?.linhas ?? []).map((l) => [l.categoria_id, l]));
      for (const l of q.data?.por_categoria ?? []) {
        const id = l.categoria_id ?? "sem-categoria";
        const c = cats.get(id);
        const n = nomes.get(l.categoria_id);
        const r = nova(id, null, c
          ? { id, codigo: c.codigo, nome: c.nome }
          : { id, codigo: n?.categoria_codigo ?? null, nome: n?.categoria_nome ?? "(categoria fora do orçamento)" });
        const m = Number(l.competencia.slice(5, 7)) - 1;
        r.meses[m] = somarValores([r.meses[m]!, {
          orcado: BigInt(l.orcado_centavos), realizado: big(l.realizado_centavos), comprometido: big(l.comprometido_centavos),
        }]);
      }
      return [...porChave.values()];
    }
    for (const l of q.data?.linhas ?? []) {
      const r = nova(`${l.centro_custo_id}|${l.categoria_id}`,
        { id: l.centro_custo_id, codigo: l.centro_custo_codigo, nome: l.centro_custo_nome },
        { id: l.categoria_id ?? "sem-categoria", codigo: l.categoria_codigo, nome: l.categoria_nome });
      const m = Number(l.competencia.slice(5, 7)) - 1;
      r.meses[m] = somarValores([r.meses[m]!, {
        orcado: BigInt(l.orcado_centavos), realizado: big(l.realizado_centavos), comprometido: big(l.comprometido_centavos),
      }]);
    }
    for (const c of q.data?.conciliacao ?? []) {
      const r = porChave.get(`${c.centro_custo_id}|${c.categoria_id}`);
      if (!r) continue;
      r.conc[Number(c.competencia.slice(5, 7)) - 1] = c.qtd_lancamentos === null
        ? null
        : { lanc: c.qtd_lancamentos, auto: c.qtd_automatico ?? 0, pessoa: c.qtd_confirmado ?? 0, aguardando: c.qtd_aguardando ?? 0, sem: c.qtd_sem_alocacao ?? 0 };
    }
    return [...porChave.values()];
  }, [q.data, receitaPorCategoria]);

  // Os centros de custo e as categorias que existem, para os filtros.
  const ccs = useMemo(() => {
    const m = new Map<string, Ref>();
    for (const r of base) if (r.cc) m.set(r.cc.id, r.cc);
    return [...m.values()].sort(porCodigo);
  }, [base]);
  const cats = useMemo(() => {
    const m = new Map<string, Ref>();
    for (const r of base) m.set(r.cat.id, r.cat);
    return [...m.values()].sort(porCodigo);
  }, [base]);

  // Colunas: os meses marcados no filtro, ou os doze.
  const colunas = visao === "ano"
    ? MESES.map((_, i) => i)
    : periodo.filter((c) => c.slice(0, 4) === String(ano)).map((c) => Number(c.slice(5, 7)) - 1).sort((a, b) => a - b);
  // Comprometido, saldo, consumo e status só cabem com um mês só na tela.
  const extras = visao === "mes" && colunas.length === 1;

  // Mês futuro não tem realizado: mostrar "0,00" sugeriria que nada foi gasto.
  const hoje = new Date();
  const mesFuturo = (m: number) =>
    ano > hoje.getFullYear() || (ano === hoje.getFullYear() && m > hoje.getMonth());
  const realizadoDe = (v: Valores, m: number) => (mesFuturo(m) ? null : v.realizado ?? 0n);

  // "Categorias com valor orçado ou realizado no período" (item 1.3). O
  // comprometido não conta: ele não tem coluna, e a linha só com comprometido
  // apareceria zerada.
  const temValor = (r: Linha) => colunas.some((m) => {
    const v = r.meses[m]!;
    return v.orcado !== 0n || (realizadoDe(v, m) ?? 0n) !== 0n;
  });
  const concNoPeriodo = (r: Linha) => somarConc(colunas.map((m) => r.conc[m]));

  // Filtros sobre as linhas base. Linha sem valor nos meses da tela não vem.
  const comValor = base.filter((r) =>
    (ccFiltro === "todos" || receitaPorCategoria || r.cc?.id === ccFiltro)
    && (catFiltro === "todas" || r.cat.id === catFiltro)
    && (receitaPorCategoria || concFiltro === "todas" || (() => {
      // "Pendente": há sugestão esperando o Financeiro nos meses da tela.
      // "Automático": há lançamento que o motor confirmou sozinho.
      const c = concNoPeriodo(r);
      if (!c) return false;
      return concFiltro === "pendentes" ? c.aguardando > 0 : c.auto > 0;
    })())
    && temValor(r));

  // Os grupos da grade.
  const grupos: Grupo[] = (() => {
    const m = new Map<string, Grupo & { ordem: string | number }>();
    for (const r of comValor) {
      let chave: string, rotulo: string, cat: Ref | null = null, ordem: string | number;
      if (receitaPorCategoria) {
        const g = grupoReceita(r.cat.codigo);
        chave = g.chave; rotulo = g.rotulo; ordem = g.ordem;
      } else if (agrupar === "cc") {
        chave = r.cc!.id; rotulo = `${r.cc!.codigo} · ${r.cc!.nome}`; ordem = r.cc!.codigo ?? "~";
      } else {
        chave = r.cat.id; rotulo = `${r.cat.codigo ? `${r.cat.codigo} · ` : ""}${r.cat.nome}`; cat = r.cat; ordem = r.cat.codigo ?? "~";
      }
      if (!m.has(chave)) m.set(chave, { chave, rotulo, cat, linhas: [], ordem });
      m.get(chave)!.linhas.push(r);
    }
    const lista = [...m.values()];
    for (const g of lista) {
      g.linhas.sort((a, b) => (agrupar === "categoria" && !receitaPorCategoria ? porCodigo(a.cc!, b.cc!) : porCodigo(a.cat, b.cat)));
    }
    return lista.sort((a, b) => (typeof a.ordem === "number" && typeof b.ordem === "number"
      ? a.ordem - b.ordem
      : String(a.ordem).localeCompare(String(b.ordem), undefined, { numeric: true })));
  })();

  const rotuloLinha = (r: Linha) =>
    agrupar === "categoria" && !receitaPorCategoria ? { codigo: r.cc!.codigo, nome: r.cc!.nome } : { codigo: r.cat.codigo, nome: r.cat.nome };

  function exportar() {
    const cab = receitaPorCategoria ? ["Grupo", "Categoria"] : ["Centro de custo", "Categoria"];
    for (const m of colunas) {
      cab.push(`${MESES[m]}/${ano} Orçado`, `${MESES[m]}/${ano} Realizado`);
      if (!extras) cab.push(`${MESES[m]}/${ano} Variação (%)`);
    }
    if (extras) cab.push("Variação (R$)", "Variação (%)");
    else cab.push("Total do ano Orçado", "Total do ano Realizado", "Variação até o mês corrente (R$)", "Variação até o mês corrente (%)");
    const linhas = [cab];
    for (const g of grupos) {
      for (const r of g.linhas) {
        const l = [
          r.cc ? `${r.cc.codigo} · ${r.cc.nome}` : g.rotulo,
          `${r.cat.codigo ? r.cat.codigo + " " : ""}${r.cat.nome}`,
        ];
        for (const m of colunas) {
          const v = r.meses[m]!;
          const re = realizadoDe(v, m);
          l.push(centavosParaCsv(v.orcado.toString()), re === null ? "" : centavosParaCsv(re.toString()));
          if (!extras) l.push(pctCsv(v.orcado, re));
        }
        if (extras) {
          const m = colunas[0]!, v = r.meses[m]!;
          const re = realizadoDe(v, m);
          l.push(re === null ? "" : centavosParaCsv((re - v.orcado).toString()), pctCsv(v.orcado, re));
        } else {
          const t = totais(r.meses);
          l.push(
            centavosParaCsv(t.orcado.toString()),
            t.realizado === null ? "" : centavosParaCsv(t.realizado.toString()),
            t.realizado === null ? "" : centavosParaCsv((t.realizado - t.orcadoAteHoje).toString()),
            pctCsv(t.orcadoAteHoje, t.realizado),
          );
        }
        linhas.push(l);
        // A linha aberta vai junto, com o que a tela já carregou.
        if (r.cc && natureza === "despesa" && abertas.has(r.chave)) {
          const d = queryClient.getQueryData<{ linhas: RealizadoFornecedor[]; orcado?: OrcadoFornecedor[] }>(chaveFornecedores(ano, r.cc.id, r.cat.id));
          const csv = (v: bigint) => (v === 0n ? "" : centavosParaCsv(v.toString()));
          for (const f of agruparPorFornecedor(d?.linhas ?? [], d?.orcado ?? [])) {
            const lf = [l[0]!, `${l[1]} › ${nomeFornecedor(f)}`];
            let total = 0n;
            for (const m of colunas) {
              const re = mesFuturo(m) ? 0n : f.realizado[m]!;
              lf.push(csv(f.orcado[m]!), csv(re));
              if (!extras) lf.push("");
            }
            for (let m = 0; m < 12; m++) if (!mesFuturo(m)) total += f.realizado[m]!;
            if (extras) lf.push("", "");
            else lf.push(csv(f.totalOrcado), centavosParaCsv(total.toString()), "", "");
            linhas.push(lf);
          }
        }
      }
    }
    const ccSufixo = ccFiltro === "todos" || receitaPorCategoria ? "" : `_${ccs.find((c) => c.id === ccFiltro)?.codigo ?? ""}`;
    const periodoSufixo = visao === "ano" ? "anual" : colunas.map((m) => String(m + 1).padStart(2, "0")).join("-");
    baixarCsv(`orcado_vs_realizado_${ano}_${periodoSufixo}${ccSufixo}_${natureza}${agrupar === "categoria" ? "_por_categoria" : ""}.csv`, linhas);
  }

  /** Variação percentual: nula sem orçado (não há base) e sem realizado. */
  const variacaoPct = (orcado: bigint, realizado: bigint | null): number | null =>
    realizado === null || orcado === 0n ? null : Number(((realizado - orcado) * 10000n) / orcado) / 100;
  const pctCsv = (orcado: bigint, realizado: bigint | null) => {
    const p = variacaoPct(orcado, realizado);
    return p === null ? "" : p.toFixed(2).replace(".", ",");
  };

  /** O total do ano, e a variação SÓ dos meses que já passaram — comparar o
   *  realizado de outubro com o orçado de dezembro daria sempre "abaixo". */
  function totais(meses: Valores[]) {
    let orcado = 0n, orcadoAteHoje = 0n;
    let realizado: bigint | null = null;
    for (let m = 0; m < meses.length; m++) {
      const v = meses[m]!;
      orcado += v.orcado;
      const re = realizadoDe(v, m);
      if (re !== null) { realizado = (realizado ?? 0n) + re; orcadoAteHoje += v.orcado; }
    }
    // Ano que ainda não começou não tem realizado: "—", não "0,00".
    return { orcado, realizado, orcadoAteHoje };
  }

  /** Despesa acima do orçado é ruim; receita acima, boa. */
  const corVariacao = (d: bigint) =>
    d === 0n ? "" : (d > 0n) === (natureza === "despesa") ? "text-risco-tinta" : "text-ok-tinta";

  const celulaVariacaoReais = (orcado: bigint, realizado: bigint | null, classe = "") => {
    if (realizado === null) return <td className={`num px-3 py-1.5 text-right text-tinta-fraca ${classe}`}>—</td>;
    const d = realizado - orcado;
    return <td className={`num px-3 py-1.5 text-right whitespace-nowrap ${corVariacao(d)} ${classe}`}>{comSinal(d)}</td>;
  };
  const celulaVariacaoPct = (orcado: bigint, realizado: bigint | null, classe = "") => {
    const p = variacaoPct(orcado, realizado);
    if (p === null) return <td className={`num px-3 py-1.5 text-right text-tinta-fraca ${classe}`}>—</td>;
    const d = realizado! - orcado;
    const arred = Math.round(p);
    return (
      <td className={`num px-3 py-1.5 text-right whitespace-nowrap ${corVariacao(d)} ${classe}`}>
        {arred > 0 ? "+" : ""}{arred}%
      </td>
    );
  };

  const celulas = (v: Valores, m: number, nota?: ReactNode) => {
    const re = realizadoDe(v, m);
    const t = tom(v.orcado, re);
    return (
      <>
        <td className="num border-l border-linha-forte px-2 py-1.5 text-right">{v.orcado === 0n ? "—" : curto(v.orcado)}</td>
        <td className={`num px-2 py-1.5 text-right ${re === null ? "text-tinta-fraca" : natureza === "despesa" && t ? COR_TOM[t] : ""}`}>{curto(re)}{nota}</td>
        {!extras && celulaVariacaoPct(v.orcado, re, "text-[11.5px]")}
      </>
    );
  };

  const linhaTabela = (rotulo: ReactNode, meses: Valores[], classe: string, primeira: string, nota?: (m: number) => ReactNode) => {
    let fim: ReactNode;
    if (extras) {
      const m = colunas[0]!, v = meses[m]!;
      const re = realizadoDe(v, m);
      const usado = re === null ? null : re + (v.comprometido ?? 0n);
      const t = tom(v.orcado, usado);
      fim = (
        <>
          {celulaVariacaoReais(v.orcado, re, "border-l border-linha-forte")}
          {celulaVariacaoPct(v.orcado, re)}
          {/* O status é a faixa de consumo do orçamento: só existe na despesa. */}
          {natureza === "despesa" && (
            <td className="px-3 py-1.5">
              {t && (
                <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${PILULA_TOM[t]}`}>
                  {ROTULO_TOM[t]}
                </span>
              )}
            </td>
          )}
        </>
      );
    } else {
      const t = totais(meses);
      fim = (
        <>
          <td className="num border-l border-linha-forte bg-superficie-2/50 px-2 py-1.5 text-right">{curto(t.orcado)}</td>
          <td className="num bg-superficie-2/50 px-2 py-1.5 text-right">{curto(t.realizado)}</td>
          {celulaVariacaoReais(t.orcadoAteHoje, t.realizado, "bg-superficie-2/50")}
          {celulaVariacaoPct(t.orcadoAteHoje, t.realizado, "bg-superficie-2/50")}
        </>
      );
    }
    return (
      <tr className={classe}>
        <td className={`sticky left-0 z-10 px-3 py-1.5 ${primeira}`}>{rotulo}</td>
        {colunas.map((m) => <Fragment key={m}>{celulas(meses[m]!, m, nota?.(m))}</Fragment>)}
        {fim}
        <td aria-hidden className="w-full p-0" />
      </tr>
    );
  };

  // O botão de lançamentos: um ícone, para não disputar espaço com o nome
  // (pedido de 21/10). Só com um mês na tela — o drilldown é de um mês — e só
  // na despesa: a receita sem CC não passa pelo detalhamento por coordenada.
  const podeDetalhar = (meses: Valores[]) => {
    if (!extras || natureza !== "despesa") return false;
    const m = colunas[0]!, v = meses[m]!;
    return (realizadoDe(v, m) ?? 0n) !== 0n || (v.comprometido ?? 0n) !== 0n;
  };
  const abrirDetalhe = (cc: Ref | null, cat: Ref, meses: Valores[]) => {
    const m = colunas[0]!;
    const v = meses[m]!;
    const realizado = realizadoDe(v, m) ?? 0n;
    setDetalhando({
      centro_custo_id: cc?.id ?? null,
      centro_custo_codigo: cc?.codigo ?? "",
      centro_custo_nome: cc?.nome ?? "Todos os centros de custo",
      categoria_id: cat.id === "sem-categoria" ? null : cat.id,
      categoria_nome: cat.nome,
      competencia: `${ano}-${String(m + 1).padStart(2, "0")}-01`,
      orcado_centavos: v.orcado.toString(),
      realizado_centavos: realizado.toString(),
      pct_execucao: v.orcado === 0n ? "0" : (Number((realizado * 10000n) / v.orcado) / 100).toFixed(2),
    });
  };
  const botaoDetalhe = (titulo: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      aria-label={titulo}
      className="ml-auto grid size-6 shrink-0 place-items-center rounded-md text-azul hover:bg-azul/10"
    >
      <Icone nome="lupa" tamanho={14} espessura={2.2} />
    </button>
  );

  const totalGeral = MESES.map((_, m) => somarValores(grupos.flatMap((g) => g.linhas.map((r) => r.meses[m]!))));
  const nomeGrupos = receitaPorCategoria ? "grupos" : agrupar === "cc" ? "centros de custo" : "categorias";

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          {escopo === "gestor" ? "Painel do Gestor · Analítica" : "Visão financeira"}
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Orçado vs Realizado
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          {escopo === "gestor"
            ? "Os centros de custo sob sua responsabilidade, linha a linha, mês a mês."
            : "Todos os centros de custo, por categoria, mês a mês."}
        </p>
      </header>

      <FiltrosRecolhiveis
        chave="orcado-realizado"
        resumo={[
          natureza === "receita" ? "Receita" : "Despesa e investimento",
          visao === "ano" ? `Ano completo de ${ano}` : resumoPeriodo(periodo),
          receitaPorCategoria || agrupar === "categoria" ? "por categoria" : "por centro de custo",
          !receitaPorCategoria && ccFiltro !== "todos" ? ccs.find((c) => c.id === ccFiltro)?.nome : null,
          catFiltro !== "todas" ? cats.find((c) => c.id === catFiltro)?.nome : null,
          !receitaPorCategoria && concFiltro === "pendentes" ? "pendentes de confirmação" : null,
          !receitaPorCategoria && concFiltro === "automaticas" ? "conciliados automaticamente" : null,
        ].filter(Boolean).join(" · ")}
        acoes={
          <>
            <AbrirFecharTodos grupos={grupos.map((g) => g.chave)} recolhidos={recolhidos} rotulo={nomeGrupos} />
            <button type="button" onClick={exportar} disabled={grupos.length === 0} className={CLASSE_BOTAO_BARRA}>
              Exportar CSV
            </button>
          </>
        }
      >
        <CampoCompacto rotulo="Natureza">
          <Seletor rotulo="Natureza" valor={natureza} onMudar={(v) => { setNatureza(v); setCatFiltro("todas"); }}
            opcoes={[["despesa", "Despesa e investimento"], ["receita", "Receita"]] as const} />
        </CampoCompacto>
        <CampoCompacto rotulo="Visão">
          <Seletor rotulo="Visão" valor={visao} onMudar={setVisao}
            opcoes={[["mes", "Mês selecionado"], ["ano", "Ano completo"]] as const} />
        </CampoCompacto>
        <CampoCompacto rotulo="Mês">
          <FiltroMes compacto periodo={periodo} onMudar={onMudarPeriodo} />
        </CampoCompacto>
        <CampoCompacto rotulo="Ano">
          <FiltroAno compacto periodo={periodo} onMudar={onMudarPeriodo} exercicios={[ano - 1, ano, ano + 1]} />
        </CampoCompacto>
        {natureza === "despesa" && (
          <>
            <CampoCompacto rotulo="Agrupar por">
              <Seletor rotulo="Agrupar por" valor={agruparDespesa} onMudar={(v) => { setAgrupar(v); recolhidos.abrirTodos(); }}
                opcoes={[["cc", "Centro de custo"], ["categoria", "Categoria (todos os CCs)"]] as const} />
            </CampoCompacto>
            <CampoCompacto rotulo="Centro de custo">
              <select value={ccFiltro} onChange={(e) => setCcFiltro(e.target.value)} aria-label="Centro de custo"
                disabled={ccs.length <= 1}
                className="max-w-[220px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px] disabled:bg-superficie-2 disabled:text-tinta-media">
                {ccs.length !== 1 && <option value="todos">Todos</option>}
                {ccs.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
              </select>
            </CampoCompacto>
          </>
        )}
        <CampoCompacto rotulo="Categoria">
          <select value={catFiltro} onChange={(e) => setCatFiltro(e.target.value)} aria-label="Categoria"
            className="max-w-[240px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]">
            <option value="todas">Todas</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ""}{c.nome}</option>)}
          </select>
        </CampoCompacto>
        {natureza === "despesa" && (
          <CampoCompacto rotulo="Conciliação">
            <Seletor rotulo="Conciliação" valor={concFiltro} onMudar={setConcFiltro}
              opcoes={[
                ["todas", "Todas"],
                ["pendentes", "Pendente de confirmação"],
                ["automaticas", "Conciliado automaticamente"],
              ] as const} />
          </CampoCompacto>
        )}
      </FiltrosRecolhiveis>

      {visao === "mes" && colunas.length === 0 && (
        <Vazio titulo={`Nenhum mês de ${ano} selecionado`} dica="Escolha ao menos um mês no filtro." />
      )}

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar a planilha" dica={(q.error as FalhaApi).message} />}
      {q.data && grupos.length === 0 && colunas.length > 0 && (
        <Painel>
          <Vazio
            titulo={base.length > 0 ? "Nada com esse filtro no período" : `Ainda não há orçamento em ${ano}`}
            dica={base.length > 0
              ? "Só aparecem as linhas com orçado ou realizado nos meses da tela. Troque os filtros ou o mês."
              : escopo === "gestor"
                ? "Quando o Financeiro cadastrar o orçamento dos seus centros de custo, ele aparece aqui."
                : "O orçamento é lançado em OPERACIONAL → Planilha orçamentária, nas abas Planilha, Lançar ou Importar."}
          />
        </Painel>
      )}

      {q.data && grupos.length > 0 && colunas.length > 0 && (
        <>
          {/* A legenda saiu da barra de filtros (que recolhe) e fica junto da grade que ela explica. */}
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <LegendaEstados />
            <span
              className="cursor-help text-[11.5px] text-tinta-fraca"
              title={`Meses que ainda não chegaram mostram “—” no realizado.${extras && natureza === "despesa" ? " O status soma o comprometido ao realizado." : ""} A variação compara o realizado com o orçado; no total do ano, só dos meses que já chegaram.${natureza === "despesa" ? " O selo ao lado do nome diz como os lançamentos entraram: “automático” é o que o motor confirmou sozinho; “a confirmar” espera o Financeiro. A seta ao lado da categoria abre quem recebeu, mês a mês: o pago no realizado e, embaixo, o que ainda está a pagar." : ""}`}
            >
              ⓘ Como ler
            </span>
          </div>
          <CaixaGrade>
            <table className={`w-full border-collapse text-[12.5px] ${visao === "ano" ? "min-w-[2700px]" : ""}`}>
              <thead className="sticky top-0 z-20 bg-superficie-2 text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                <tr>
                  <th rowSpan={2} className="sticky left-0 z-30 bg-superficie-2 px-3 py-2 text-left align-middle">
                    <NomeNaLargura largura={coluna.largura}>
                      {receitaPorCategoria ? "Grupo · categoria" : agrupar === "cc" ? "Centro de custo · categoria" : "Categoria · centro de custo"}
                    </NomeNaLargura>
                    <AlcaLargura coluna={coluna} />
                  </th>
                  {colunas.map((m) => (
                    <th key={m} colSpan={extras ? 2 : 3} className="border-b border-l border-linha border-l-linha-forte bg-superficie-2 px-2 pt-2 pb-1 text-center">
                      {MESES[m]}/{String(ano).slice(2)}
                    </th>
                  ))}
                  {extras ? (
                    <>
                      <th rowSpan={2} className="border-l border-linha-forte bg-superficie-2 px-3 py-2 text-center whitespace-nowrap align-middle"
                        title={natureza === "despesa" ? "Realizado menos orçado: positivo é gasto acima do orçado" : "Realizado menos orçado: positivo é receita acima do orçado"}>
                        Variação (R$)
                      </th>
                      <th rowSpan={2} className="bg-superficie-2 px-3 py-2 text-center whitespace-nowrap align-middle" title="A variação sobre o orçado">
                        Variação (%)
                      </th>
                      {natureza === "despesa" && (
                        <th rowSpan={2} className="bg-superficie-2 px-3 py-2 text-left align-middle">Status</th>
                      )}
                    </>
                  ) : (
                    <th colSpan={4} className="border-b border-l border-linha border-l-linha-forte bg-[#e3e8f1] px-2 pt-2 pb-1 text-center">
                      Total do ano
                    </th>
                  )}
                  <th rowSpan={2} aria-hidden className="w-full bg-superficie-2 p-0" />
                </tr>
                <tr className="text-[10px]">
                  {colunas.map((m) => (
                    <Fragment key={m}>
                      <th className="border-l border-linha-forte bg-superficie-2 px-2 pt-1 pb-2 text-center">Orçado</th>
                      <th className="bg-superficie-2 px-2 pt-1 pb-2 text-center">Realizado</th>
                      {!extras && <th className="bg-superficie-2 px-2 pt-1 pb-2 text-center whitespace-nowrap" title="A variação do mês sobre o orçado">Var. (%)</th>}
                    </Fragment>
                  ))}
                  {!extras && (
                    <>
                      <th className="border-l border-linha-forte bg-[#e3e8f1] px-2 pt-1 pb-2 text-center">Orçado</th>
                      <th className="bg-[#e3e8f1] px-2 pt-1 pb-2 text-center">Realizado</th>
                      <th className="bg-[#e3e8f1] px-2 pt-1 pb-2 text-center whitespace-nowrap"
                        title="Realizado menos o orçado dos meses que já chegaram — os meses à frente não entram na comparação">
                        Variação (R$)
                      </th>
                      <th className="bg-[#e3e8f1] px-2 pt-1 pb-2 text-center whitespace-nowrap"
                        title="A mesma variação, sobre o orçado dos meses que já chegaram">
                        Variação (%)
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => {
                  const sub = MESES.map((_, m) => somarValores(g.linhas.map((r) => r.meses[m]!)));
                  const aberto = !recolhidos.fechado(g.chave);
                  return (
                    <Fragment key={g.chave}>
                      {linhaTabela(
                        <NomeNaLargura largura={coluna.largura}>
                          <BotaoGrupo aberto={aberto} onClick={() => recolhidos.alternar(g.chave)} rotulo={g.rotulo} />
                          <span className="truncate" title={g.rotulo}>{g.rotulo}</span>
                          {!aberto && <span className="shrink-0 text-[11px] font-normal text-tinta-fraca">({g.linhas.length})</span>}
                          {g.cat && podeDetalhar(sub) && botaoDetalhe(
                            `Ver os lançamentos de ${g.cat.nome} em todos os centros de custo`,
                            () => abrirDetalhe(null, g.cat!, sub))}
                        </NomeNaLargura>,
                        sub, "border-t-2 border-linha-forte font-semibold", "bg-superficie-2",
                      )}
                      {aberto && g.linhas.map((r) => {
                        const rot = rotuloLinha(r);
                        const abrePorFornecedor = natureza === "despesa" && r.cc !== null;
                        const aberta = abrePorFornecedor && abertas.has(r.chave);
                        return (
                          <Fragment key={r.chave}>
                            {linhaTabela(
                              <NomeNaLargura largura={coluna.largura} className={abrePorFornecedor ? "pl-1" : "pl-6"}>
                                {abrePorFornecedor && (
                                  <BotaoGrupo aberto={aberta} onClick={() => alternarAberta(r.chave)} rotulo={`quem recebeu em ${rot.nome}`} />
                                )}
                                <span className="num shrink-0 text-tinta-fraca">{rot.codigo}</span>
                                <span className="truncate" title={rot.nome}>{rot.nome}</span>
                                {natureza === "despesa" && <SeloConciliacao c={concNoPeriodo(r)} />}
                                {r.cc && podeDetalhar(r.meses) && botaoDetalhe("Ver lançamentos", () => abrirDetalhe(r.cc, r.cat, r.meses))}
                              </NomeNaLargura>,
                              r.meses, "border-t border-linha hover:bg-superficie-2/40", "bg-superficie",
                            )}
                            {aberta && (
                              <LinhasDosFornecedores
                                desenhar={linhaTabela}
                                ano={ano}
                                centroCustoId={r.cc!.id}
                                categoriaId={r.cat.id}
                                colunas={colunas}
                                extras={extras}
                                comStatus={natureza === "despesa"}
                                largura={coluna.largura}
                                mesFuturo={mesFuturo}
                              />
                            )}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
              {/* O total fica preso no pé da caixa, como o cabeçalho no topo. */}
              <tfoot className="sticky bottom-0 z-20">
                {linhaTabela(
                  <NomeNaLargura largura={coluna.largura}>Total</NomeNaLargura>,
                  totalGeral, "border-t-2 border-linha-forte bg-superficie-2 font-bold", "bg-superficie-2",
                )}
              </tfoot>
            </table>
          </CaixaGrade>
        </>
      )}
      {detalhando && (
        <ModalLancamentos linha={detalhando} onFechar={() => setDetalhando(null)} />
      )}
    </div>
  );
}

/** A chave da linha aberta no cache: começa por "orcado-realizado", para quem
 *  invalida a grade invalidar as linhas abertas junto. */
const chaveFornecedores = (ano: number, centroCustoId: string, categoriaId: string) =>
  ["orcado-realizado", "fornecedores", ano, centroCustoId, categoriaId] as const;

const nomeFornecedor = (f: LinhaFornecedor) =>
  f.restrito ? "Pessoal: total da categoria"
    : f.chave === ORCADO_SEM_PESSOA ? "Orçado sem pessoa ligada"
      : f.nome ?? "(sem fornecedor)";

/**
 * A linha da categoria aberta: uma linha por fornecedor, com o pago de cada
 * mês na coluna Realizado e, embaixo, o que ainda está a pagar. Os meses que
 * ainda não chegaram ficam vazios, como na grade.
 *
 * Sem orçado por pessoa, as colunas de orçado e de variação ficam vazias — o
 * orçado é da categoria, não do fornecedor. Com ele (080), cada pessoa é uma
 * linha completa da grade, desenhada pela mesma função: orçado, realizado,
 * variação e situação, como as outras.
 */
function LinhasDosFornecedores({
  desenhar,
  ano,
  centroCustoId,
  categoriaId,
  colunas,
  extras,
  comStatus,
  largura,
  mesFuturo,
}: {
  desenhar: (rotulo: ReactNode, meses: Valores[], classe: string, primeira: string, nota?: (m: number) => ReactNode) => ReactNode;
  ano: number;
  centroCustoId: string;
  categoriaId: string;
  colunas: number[];
  extras: boolean;
  comStatus: boolean;
  largura: number;
  mesFuturo: (m: number) => boolean;
}) {
  const q = useQuery({
    queryKey: chaveFornecedores(ano, centroCustoId, categoriaId),
    queryFn: () => buscarRealizadoPorFornecedor(ano, centroCustoId, categoriaId === "sem-categoria" ? null : categoriaId),
  });
  const fornecedores = useMemo(() => agruparPorFornecedor(q.data?.linhas ?? [], q.data?.orcado ?? []), [q.data]);
  const porPessoa = temOrcadoPorFornecedor(q.data?.orcado ?? []);
  const resto = colunas.length * (extras ? 2 : 3) + (extras ? (comStatus ? 3 : 2) : 4);
  const nome = (conteudo: ReactNode, classe = "") => (
    <NomeNaLargura largura={largura} className={`pl-9 text-[12px] ${classe}`}>{conteudo}</NomeNaLargura>
  );
  const celulaNome = (conteudo: ReactNode, classe = "") => (
    <td className="sticky left-0 z-10 bg-superficie px-3 py-1">{nome(conteudo, classe)}</td>
  );
  const aviso = (texto: string) => (
    <tr className="border-t border-linha/60">
      {celulaNome(<span className="truncate">{texto}</span>, "text-tinta-fraca")}
      <td colSpan={resto} />
      <td aria-hidden className="w-full p-0" />
    </tr>
  );
  const aPagar = (v: bigint) => (
    <span className="block text-[10.5px] whitespace-nowrap text-tinta-fraca" title="Lançado no Conta Azul e ainda não pago">
      a pagar {curto(v)}
    </span>
  );
  const rotuloDe = (f: LinhaFornecedor) => {
    const rotulo = nomeFornecedor(f);
    return (
      <>
        <span aria-hidden className="shrink-0 text-tinta-fraca">↳</span>
        <span className={`truncate ${f.restrito || !f.nome ? "italic" : ""}`} title={rotulo}>{rotulo}</span>
      </>
    );
  };
  if (q.isPending) return aviso("Carregando quem recebeu…");
  if (q.isError) return aviso((q.error as FalhaApi).message);
  if (fornecedores.length === 0) return aviso(`Nenhum lançamento em ${ano}.`);

  return (
    <>
      {fornecedores.map((f) => {
        // Com orçado por pessoa, a pessoa (ou fornecedor ligado) é uma linha
        // inteira da grade. O "sem fornecedor" e o "orçado sem pessoa" não: a
        // variação deles não diz nada sobre ninguém.
        if (porPessoa && f.chave !== "sem-fornecedor" && f.chave !== ORCADO_SEM_PESSOA && !f.restrito) {
          const meses: Valores[] = f.orcado.map((orcado, m) => ({
            orcado, realizado: f.realizado[m]!, comprometido: f.comprometido[m]!,
          }));
          return (
            <Fragment key={f.chave}>
              {desenhar(
                nome(rotuloDe(f)),
                meses,
                "border-t border-linha/60 text-[12px] text-tinta-media hover:bg-superficie-2/40",
                "bg-superficie py-1",
                (m) => (!mesFuturo(m) && f.comprometido[m]! !== 0n ? aPagar(f.comprometido[m]!) : null),
              )}
            </Fragment>
          );
        }
        let total = 0n;
        for (let m = 0; m < 12; m++) if (!mesFuturo(m)) total += f.realizado[m]!;
        const soOrcado = f.chave === ORCADO_SEM_PESSOA;
        return (
          <tr key={f.chave} className="border-t border-linha/60 text-[12px] text-tinta-media hover:bg-superficie-2/40">
            {celulaNome(rotuloDe(f))}
            {colunas.map((m) => {
              const futuro = mesFuturo(m);
              const re = f.realizado[m]!;
              const comp = f.comprometido[m]!;
              return (
                <Fragment key={m}>
                  <td className="num border-l border-linha-forte px-2 py-1 text-right">{f.orcado[m]! !== 0n && curto(f.orcado[m]!)}</td>
                  <td className="num px-2 py-1 text-right whitespace-nowrap">
                    {!futuro && re !== 0n && curto(re)}
                    {!futuro && comp !== 0n && aPagar(comp)}
                  </td>
                  {!extras && <td />}
                </Fragment>
              );
            })}
            {extras ? (
              <>
                <td className="border-l border-linha-forte" />
                <td />
                {comStatus && <td />}
              </>
            ) : (
              <>
                <td className="num border-l border-linha-forte bg-superficie-2/50 px-2 py-1 text-right">{f.totalOrcado !== 0n && curto(f.totalOrcado)}</td>
                <td className="num bg-superficie-2/50 px-2 py-1 text-right">{soOrcado || total === 0n ? "" : curto(total)}</td>
                <td className="bg-superficie-2/50" />
                <td className="bg-superficie-2/50" />
              </>
            )}
            <td aria-hidden className="w-full p-0" />
          </tr>
        );
      })}
    </>
  );
}

/**
 * Como os lançamentos da linha entraram (migration 064), somando os meses da
 * tela. O que espera o Financeiro vem primeiro; depois, o que não está em linha
 * nenhuma; e só então o que já está conciliado.
 */
function SeloConciliacao({ c }: { c: Conc | null | undefined }) {
  if (!c || c.lanc === 0) return null;
  const dica = [
    `${c.auto} conciliado${c.auto === 1 ? "" : "s"} automaticamente`,
    `${c.pessoa} confirmado${c.pessoa === 1 ? "" : "s"} pelo Financeiro`,
    `${c.aguardando} aguardando confirmação`,
    `${c.sem} sem linha orçada`,
  ].join(" · ");
  const [texto, classe] = c.aguardando > 0
    ? [`${c.aguardando} a confirmar`, "border-atencao-linha bg-atencao-bg text-atencao-tinta"]
    : c.sem > 0
      ? [`${c.sem} sem linha`, "border-neutro-linha bg-neutro-bg text-neutro-tinta"]
      : c.auto === c.lanc
        ? ["✓ automático", "border-ok-linha bg-ok-bg text-ok-tinta"]
        : ["✓ conciliado", "border-ok-linha bg-ok-bg text-ok-tinta"];
  return (
    <span title={dica} className={`shrink-0 rounded-full border px-1.5 py-px text-[10px] font-semibold whitespace-nowrap ${classe}`}>
      {texto}
    </span>
  );
}
